import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AssessmentResult, ParticipantInfo, SelfAssessment, AssessmentLevel } from '../models/assessment.model';

@Injectable({
  providedIn: 'root'
})
export class AssessmentService {
  private assessmentData = new BehaviorSubject<Partial<AssessmentResult>>({});
  public assessmentData$ = this.assessmentData.asObservable();

  private assessmentLevels: AssessmentLevel[] = [
    {
      name: 'Seed Level',
      range: [8, 15],
      message: 'You are at the Seed Level. Thank you for being part of the foundation.',
      table: 'Table A',
      description: 'Learn how you can engage and contribute to community building.'
    },
    {
      name: 'Settlement Level',
      range: [16, 24],
      message: 'You are at the Settlement Level. You\'re building stability.',
      table: 'Table B',
      description: 'See current projects you can support and participate in.'
    },
    {
      name: 'Organisation Level',
      range: [25, 33],
      message: 'You are at the Organisation Level. Your skills and networks matter.',
      table: 'Table C',
      description: 'Form or join expert teams and contribute your specialized knowledge.'
    },
    {
      name: 'Leadership Layer',
      range: [34, 40],
      message: 'You are in the Leadership Layer.',
      table: 'Table D',
      description: 'Help shape strategy and represent the community in key decisions.'
    }
  ];

  updateParticipantInfo(info: ParticipantInfo): void {
    const current = this.assessmentData.value;
    this.assessmentData.next({
      ...current,
      participantInfo: info
    });
  }

  updateSelfAssessment(assessment: SelfAssessment): void {
    const current = this.assessmentData.value;
    const totalScore = this.calculateTotalScore(assessment);
    const level = this.determineLevel(totalScore);
    
    this.assessmentData.next({
      ...current,
      selfAssessment: assessment,
      totalScore,
      level,
      timestamp: new Date()
    });
  }

  private calculateTotalScore(assessment: SelfAssessment): number {
    return Object.values(assessment).reduce((sum, value) => sum + value, 0);
  }

  private determineLevel(score: number): 'seed' | 'settlement' | 'organisation' | 'leadership' {
    if (score >= 8 && score <= 15) return 'seed';
    if (score >= 16 && score <= 24) return 'settlement';
    if (score >= 25 && score <= 33) return 'organisation';
    return 'leadership';
  }

  getAssessmentLevel(level: string): AssessmentLevel | undefined {
    return this.assessmentLevels.find(l => l.name.toLowerCase().includes(level));
  }

  getCurrentAssessment(): Partial<AssessmentResult> {
    return this.assessmentData.value;
  }

  resetAssessment(): void {
    this.assessmentData.next({});
  }
}